import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, Plus, Wrench, Loader2 } from 'lucide-react';

export default function VehicleProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => client.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => toast.error('Failed'));
  useEffect(() => { load(); }, [id]);
  if (!v) return <AppShell title="Vehicle"><Skeleton className="h-40" /></AppShell>;
  return (
    <AppShell title={`Unit #${v.unit_number}`}>
      <button onClick={() => navigate('/fleet')} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" />Back to Fleet</button>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-1">
          <div className="font-display text-lg font-semibold">#{v.unit_number}</div>
          <div className="mb-3 text-sm text-muted-foreground">{v.make} {v.model} · {v.year}</div>
          <StatusBadge status={v.status} />
          <dl className="mt-4 space-y-2 text-sm">
            {[['Type', v.unit_type?.replace('_',' ')], ['VIN', v.vin], ['Plate', `${v.plate} (${v.plate_state})`], ['Ownership', v.ownership], ['Registration Exp', v.registration_expiry ? new Date(v.registration_expiry).toLocaleDateString() : '-'], ['Annual Inspection', v.annual_inspection_expiry ? new Date(v.annual_inspection_expiry).toLocaleDateString() : '-']].map(([k, val]) => (
              <div key={k} className="flex justify-between border-b py-1.5 last:border-0"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium">{val}</dd></div>
            ))}
          </dl>
        </Card>
        <Card className="p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between"><div className="flex items-center gap-2 font-display font-semibold"><Wrench className="h-4 w-4" />Maintenance History</div><Button size="sm" onClick={() => setOpen(true)} data-testid="add-maintenance-button"><Plus className="mr-1.5 h-4 w-4" />Log Service</Button></div>
          {(v.maintenance || []).length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No maintenance records.</p> : (
            <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Service</TableHead><TableHead>Odometer</TableHead><TableHead>Cost</TableHead></TableRow></TableHeader>
              <TableBody>{v.maintenance.map((m) => <TableRow key={m.id}><TableCell>{m.date ? new Date(m.date).toLocaleDateString() : '-'}</TableCell><TableCell>{m.service_type}</TableCell><TableCell>{m.odometer || '-'}</TableCell><TableCell>{m.cost ? `$${m.cost}` : '-'}</TableCell></TableRow>)}</TableBody>
            </Table>
          )}
        </Card>
      </div>
      <MaintDialog open={open} onOpenChange={setOpen} vehicleId={id} onAdded={load} />
    </AppShell>
  );
}

function MaintDialog({ open, onOpenChange, vehicleId, onAdded }) {
  const [f, setF] = useState({ service_type: '', odometer: '', cost: '', vendor: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!f.service_type) { toast.error('Service type required'); return; }
    setLoading(true);
    try { await client.post('/maintenance', { vehicle_id: vehicleId, service_type: f.service_type, odometer: f.odometer ? Number(f.odometer) : null, cost: f.cost ? Number(f.cost) : null, vendor: f.vendor, notes: f.notes }); toast.success('Logged'); onAdded(); onOpenChange(false); setF({ service_type: '', odometer: '', cost: '', vendor: '', notes: '' }); }
    catch { toast.error('Failed'); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader><DialogTitle>Log Maintenance</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Service Type *</Label><Input value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value })} className="mt-1" data-testid="maint-service-input" /></div>
        <div className="grid grid-cols-2 gap-3"><div><Label>Odometer</Label><Input type="number" value={f.odometer} onChange={(e) => setF({ ...f, odometer: e.target.value })} className="mt-1" /></div><div><Label>Cost ($)</Label><Input type="number" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} className="mt-1" /></div></div>
        <div><Label>Vendor</Label><Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} className="mt-1" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={loading} data-testid="maint-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
